"use client"

import { HttpTypes } from "@medusajs/types"
import { clx } from "@medusajs/ui"
import Image from "next/image"
import { useCallback, useEffect, useMemo, useState } from "react"

type ImageGalleryProps = {
  product: HttpTypes.StoreProduct
}

/** Product gallery — accessible thumbnails (real buttons with labels) and labelled arrows. */
const ImageGallery = ({ product }: ImageGalleryProps) => {
  const images = useMemo(() => {
    const list = product?.images?.length ? product.images : product?.thumbnail ? [{ id: "thumbnail", url: product.thumbnail } as any] : []
    return list
  }, [product])
  const [index, setIndex] = useState(0)
  const selected = images[Math.min(index, images.length - 1)]
  const alt = (selected?.metadata?.alt as string) || product?.title || ""

  const move = useCallback(
    (dir: "left" | "right") => setIndex((i) => Math.max(0, Math.min(images.length - 1, dir === "left" ? i - 1 : i + 1))),
    [images.length]
  )

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (document.activeElement instanceof HTMLInputElement || document.activeElement instanceof HTMLTextAreaElement) return
      if (e.key === "ArrowLeft") move("left")
      if (e.key === "ArrowRight") move("right")
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [move])

  if (!selected) return null

  return (
    <div className="flex flex-col gap-4 bg-elevate-grey-light rounded-image p-4 small:p-6">
      <div className="relative aspect-square w-full overflow-hidden rounded-image bg-white">
        <Image
          key={selected.url}
          src={selected.url}
          priority
          fetchPriority="high"
          width={800}
          height={800}
          alt={alt}
          className="absolute inset-0 w-full h-full object-contain p-6"
          sizes="(max-width: 768px) 90vw, 45vw"
        />
      </div>

      {images.length > 1 && (
        <div className="flex items-center justify-between gap-3">
          <ul className="flex flex-row gap-2 overflow-x-auto no-scrollbar" aria-label="Product images">
            {images.map((image: any, i: number) => (
              <li key={image.id}>
                <button
                  type="button"
                  onClick={() => setIndex(i)}
                  aria-label={`Show image ${i + 1} of ${images.length}`}
                  aria-current={i === index ? "true" : undefined}
                  className={clx(
                    "h-14 w-14 rounded-image overflow-hidden border bg-white flex items-center justify-center",
                    i === index ? "border-elevate-black" : "border-elevate-border opacity-70 hover:opacity-100"
                  )}
                >
                  <Image src={image.url} alt={`${product?.title ?? "Product"} - thumbnail ${i + 1}`} loading="lazy" height={56} width={56} className="object-contain h-full w-full p-1" />
                </button>
              </li>
            ))}
          </ul>
          <div className="flex gap-2 shrink-0">
            <button type="button" aria-label="Previous image" disabled={index === 0} onClick={() => move("left")} className="h-10 w-10 rounded-full border border-elevate-border bg-white disabled:opacity-40">‹</button>
            <button type="button" aria-label="Next image" disabled={index === images.length - 1} onClick={() => move("right")} className="h-10 w-10 rounded-full border border-elevate-border bg-white disabled:opacity-40">›</button>
          </div>
        </div>
      )}
    </div>
  )
}

export default ImageGallery
